/**
 * @file DetailsTab.js
 * @description Component for rendering the details tab of the client page.
 **/
import { useState, useEffect } from 'react';
import SicCodeDropdown from '../shared/asyncDropdowns/SicCodeDropdown';
import ClientTierDropdown from '../shared/asyncDropdowns/ClientTierDropdown';
import ClientStatusDropdown from '../shared/asyncDropdowns/ClientStatusDropdown';
import AgencyDropdown from '../shared/asyncDropdowns/AgencyDropdown';
import AgencyOfficeDropdown from '../shared/asyncDropdowns/AgencyOfficeDropdown';
// import PopulationRangeDropdown from "../shared/asyncDropdowns/PopulationRangeDropdown"; //"No longer using this field but may use again in the future.  Please hide vs delete"
import UsStatesTerritories from '../shared/asyncDropdowns/UsStatesTerritories';
import style from './AddClientPage.module.scss';
import { formatZipCode } from '../../utils/utils';
import CustomDatePicker from '../helpers/CustomDatePicker_V2';
import { getAgency } from '../../services/systemService';
import { getText } from '../../i18n';

/**
 * Renders a form for displaying and editing client details.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.data - The initial data for the form.
 * @param {Function} props.onChange - The callback function to be called when the form data changes.
 * @returns {JSX.Element} The rendered DetailsTab component.
 */
const DetailsTab = ({ data, onChange }) => {
    const [formData, setFormData] = useState(data);
    const [allowedTiers, setAllowedTiers] = useState([]);

    useEffect(() => {
        if (data.agencyId && data.agencyId !== formData.agencyId) {
            setFormData(data);
        }
    }, [data]);

    useEffect(() => {
        const loadAllowedTiers = async () => {
            if (formData.agencyId) {
                const agencyData = await getAgency(formData.agencyId);
                if (agencyData?.tenantTiers.length > 0) {
                    setAllowedTiers(agencyData.tenantTiers);
                } else {
                    setAllowedTiers([]);
                }
            }
        };
        loadAllowedTiers();
    }, [formData.agencyId]);

    useEffect(() => {
        if (JSON.stringify(formData) !== JSON.stringify(data)) {
            onChange(formData);
        }
    }, [formData, data, onChange]);

    const handleChange = e => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleDropdownChange = async (name, value) => {
        if (name === 'agencyId') {
            const agencyData = await getAgency(value);
            if (agencyData?.tenantTiers.length > 0) {
                setAllowedTiers(agencyData.tenantTiers);
                setFormData(prevData => {
                    const shouldResetTier =
                        prevData.tenantTierId &&
                        !agencyData.tenantTiers.includes(prevData.tenantTierId);
                    return {
                        ...prevData,
                        agencyOfficeId: null,
                        ...(shouldResetTier && { tenantTierId: null }),
                    };
                });
            } else {
                setAllowedTiers([]);
                setFormData(prevData => ({
                    ...prevData,
                    agencyOfficeId: null,
                }));
            }
        }
        setFormData(prevData => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleDateChange = (field, date) => {
        setFormData(prevData => ({
            ...prevData,
            [field]: date,
        }));
    };

    return (
        <div className={`row mx-3 pb-5 ${style.detailsTab}`}>
            <div className="col-12 col-xs-12 col-md-12 col-lg-12 col-xl-6">
                <div className="form-group mb-3 flex-lg-nowrap inline-input">
                    <label htmlFor="clientCode">
                        {getText('msg_6346915c34e2')} <span className="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        id="clientCode"
                        name="code"
                        className="form-control"
                        value={formData.code || ''}
                        onChange={handleChange}
                        required
                        placeholder={getText('msg_d783bcb01222')}
                    />
                </div>

                <div className="form-group mb-3 flex-lg-nowrap inline-input">
                    <label htmlFor="clientName">
                        {getText('msg_12e281345750')} <span className="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        id="clientName"
                        name="name"
                        className="form-control"
                        value={formData.name || ''}
                        onChange={handleChange}
                        required
                        placeholder={getText('msg_29f4e06aa3da')}
                    />
                </div>
                <div className="form-group mb-3 flex-lg-nowrap inline-input">
                    <label htmlFor="preferredClientName">{getText('msg_6df7d8380ffc')}</label>
                    <input
                        type="text"
                        id="preferredClientName"
                        name="preferredName"
                        className="form-control"
                        value={formData.preferredName || ''}
                        onChange={handleChange}
                        placeholder={getText('msg_a7a1fc6a453a')}
                    />
                </div>
                <div className="form-group">
                    <SicCodeDropdown
                        selectedSicCode={formData.sicCodeId}
                        onSicCodeChange={sicCodeId =>
                            handleDropdownChange('sicCodeId', sicCodeId)
                        }
                        inlineLabel={true}
                        isRequired={false}
                        allowSearch={true}
                    />
                </div>
                <div className="form-group mb-3 flex-lg-nowrap inline-input">
                    <label htmlFor="clientExpirationDate">
                        {getText('msg_29a152dbe5f2')}
                    </label>
                    <CustomDatePicker
                        selectedDate={formData.expirationDate}
                        onDateChange={date =>
                            handleDateChange('expirationDate', date)
                        }
                    />
                </div>
                <div className="form-group">
                    <AgencyDropdown
                        selectedAgency={formData.agencyId}
                        onAgencyChange={agencyId =>
                            handleDropdownChange('agencyId', agencyId)
                        }
                        inlineLabel={true}
                        allowNullSelection={true}
                        isRequired
                    />
                </div>
                <div className="form-group">
                    <AgencyOfficeDropdown
                        selectedOffice={formData.agencyOfficeId}
                        agencyId={formData.agencyId}
                        onOfficeChange={officeId =>
                            handleDropdownChange('agencyOfficeId', officeId)
                        }
                        inlineLabel={true}
                    />
                </div>
                <div className="form-group">
                    <ClientTierDropdown
                        selectedClientTier={formData.tenantTierId}
                        onClientTierChange={tierId =>
                            handleDropdownChange('tenantTierId', tierId)
                        }
                        inlineLabel={true}
                        isRequired
                        allowedTiers={allowedTiers}
                        label={getText('msg_5bd44ebe63cd')}
                        defaultLabel={getText('msg_8026941c843e')}
                    />
                </div>
                <div className="form-group">
                    <ClientStatusDropdown
                        selectedClientStatus={formData.tenantStatusId}
                        onClientStatusChange={statusId =>
                            handleDropdownChange('tenantStatusId', statusId)
                        }
                        inlineLabel={true}
                        isRequired
                        label={getText('msg_bae7d5be7082')}
                        defaultLabel={getText('msg_f409e9bea59c')}
                    />
                </div>
            </div>

            <div className="col-12 col-xs-12 col-md-12 col-lg-12 col-xl-6 align-self-baseline">
                <div className="form-group mb-3 flex-lg-nowrap inline-input">
                    <label htmlFor="address">{getText('msg_d70f93df5e8f')}</label>
                    <input
                        type="text"
                        className="form-control"
                        id="address"
                        name="address"
                        value={formData.address?.address || ''}
                        onChange={e =>
                            setFormData(prevData => ({
                                ...prevData,
                                address: {
                                    ...prevData.address,
                                    address: e.target.value,
                                },
                            }))
                        }
                        required
                        placeholder={getText('msg_49c17b7ac019')}
                    />
                </div>
                <div className="form-group mb-3 flex-lg-nowrap inline-input">
                    <label htmlFor="address2"></label>
                    <input
                        type="text"
                        className="form-control"
                        id="address2"
                        name="address2"
                        value={formData.address?.address2 || ''}
                        onChange={e =>
                            setFormData(prevData => ({
                                ...prevData,
                                address: {
                                    ...prevData.address,
                                    address2: e.target.value,
                                },
                            }))
                        }
                        required
                        placeholder={getText('msg_698a7ac046d3')}
                    />
                </div>
                <div className="form-group mb-3 flex-lg-nowrap inline-input">
                    <label htmlFor="city">{getText('msg_4271627f4f0b')}</label>
                    <input
                        type="text"
                        className="form-control"
                        id="city"
                        name="city"
                        value={formData.address?.city || ''}
                        onChange={e =>
                            setFormData(prevData => ({
                                ...prevData,
                                address: {
                                    ...prevData.address,
                                    city: e.target.value,
                                },
                            }))
                        }
                        required
                        placeholder={getText('msg_8b156d5a51b1')}
                    />
                </div>
                <div className="form-group">
                    <UsStatesTerritories
                        selectedState={formData.address?.state || ''}
                        onStateChange={state =>
                            setFormData(prevData => ({
                                ...prevData,
                                address: {
                                    ...prevData.address,
                                    state: state,
                                },
                            }))
                        }
                        isRequired={false}
                        inlineLabel={true}
                    />
                </div>

                <div className="form-group mb-3 flex-lg-nowrap inline-input">
                    <label htmlFor="zip">{getText('msg_d9d220ab2925')}</label>

                    <input
                        type="text"
                        id="zip"
                        name="zip"
                        className="form-control"
                        value={formData.address?.zip || ''}
                        onChange={e => {
                            setFormData(prevData => ({
                                ...prevData,
                                address: {
                                    ...prevData.address,
                                    zip: formatZipCode(e.target.value),
                                },
                            }));
                        }}
                        maxLength="10"
                        placeholder={getText('msg_f9f727801383')}
                    />
                </div>
            </div>
        </div>
    );
};

export default DetailsTab;
